// src/manager/pages/Cashouts.tsx — FINAL RTP INTEGRATED VERSION
// Real-time sync, optimistic actions, RTP warnings, toast feedback, dollar format

import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { ChevronDown, ChevronUp, Loader2, Search } from 'lucide-react';
import { useManager, type Cashout } from '../hooks/useManager';
import { useModal } from '../../manager/utils/modal';
import ConfirmModal from '../../shared/components/ConfirmModal';
import { toast } from 'react-hot-toast';

export default function Cashouts() {
  const { fetchCashouts, approveCashout, rejectCashout, socket } = useManager();
  const [cashouts, setCashouts] = useState<Cashout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof Cashout | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const { isOpen, message, onConfirm, showModal, closeModal } = useModal();

  // === INITIAL LOAD ===
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchCashouts();
        setCashouts(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load cashouts');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchCashouts]);

  // === REAL-TIME SOCKET UPDATES (RTP + Cashout) ===
  useEffect(() => {
    if (!socket) return;

    const handleCashoutUpdate = async () => {
      try {
        const data = await fetchCashouts();
        setCashouts(data);
        toast('Cashout request updated in real-time');
      } catch (err) {
        console.error('Real-time cashout update failed:', err);
      }
    };

    const handleRtpUpdate = (data: { tableId: string; rtp: number }) => {
      toast(`RTP updated for table ${data.tableId}: ${data.rtp}% — check cashouts`);
    };

    const handleJackpot = (data: { tableId: string; amount: number }) => {
      toast.success(`Jackpot win on table ${data.tableId}: $${data.amount} — cashouts may be affected`);
    };

    socket.on('cashout-updated', handleCashoutUpdate);
    socket.on('rtp-updated', handleRtpUpdate);
    socket.on('jackpot-win', handleJackpot);

    return () => {
      socket.off('cashout-updated', handleCashoutUpdate);
      socket.off('rtp-updated', handleRtpUpdate);
      socket.off('jackpot-win', handleJackpot);
    };
  }, [socket, fetchCashouts]);

  // === SORTING ===
  const handleSort = (column: keyof Cashout) => {
    if (sortColumn === column) {
      setSortAsc((prev) => !prev);
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
  };

  const sortedAndFiltered = [...cashouts]
    .filter((c) => c.user_email.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (!sortColumn) return 0;
      let aVal: any = a[sortColumn];
      let bVal: any = b[sortColumn];

      if (sortColumn === 'amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      } else if (sortColumn === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      return sortAsc ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

  // === APPROVE / REJECT with RTP WARNING ===
  const handleAction = (id: string, action: 'approve' | 'reject') => {
    const actionText = action === 'approve' ? 'Approve' : 'Reject';
    const cashout = cashouts.find(c => c.id === id);
    if (!cashout) return;

    const confirmMessage = `Are you sure you want to ${actionText.toLowerCase()} this cashout of $${cashout.amount.toFixed(2)} for ${cashout.user_email}?\n\n${action === 'approve' ? 'This will reduce house balance and affect RTP exposure.' : 'This will block the payout and impact RTP calculations.'}`;

    showModal(confirmMessage, async () => {
      try {
        // Optimistic update
        setCashouts(prev =>
          prev.map(c => c.id === id ? { ...c, status: action === 'approve' ? 'approved' : 'rejected' } : c)
        );

        if (action === 'approve') {
          await approveCashout(id);
          toast.success('Cashout approved');
        } else {
          await rejectCashout(id);
          toast.success('Cashout rejected');
        }
      } catch (err: any) {
        // Revert optimistic
        setCashouts(prev =>
          prev.map(c => c.id === id ? { ...c, status: 'pending' } : c)
        );
        setError(`Failed to ${actionText.toLowerCase()}: ${err.message || 'Unknown error'}`);
        toast.error(`Failed to ${actionText.toLowerCase()}: ${err.message}`);
      } finally {
        closeModal();
      }
    });
  };

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-6 text-gray-900">My Cashout Requests</h2>

        {/* ERROR */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
              ✕
            </button>
          </div>
        )}

        {/* SEARCH */}
        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by user email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition shadow-sm"
          />
        </div>

        {/* TABLE */}
        <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="animate-spin mx-auto text-indigo-600" size={40} />
              <p className="mt-4 text-gray-600 font-medium">Loading your cashout requests...</p>
            </div>
          ) : sortedAndFiltered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg">No cashout requests found {search ? `matching "${search}"` : ''}.</p>
              <p className="mt-2">New requests will appear here in real-time.</p>
            </div>
          ) : (
            <table className="w-full table-auto">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
                <tr>
                  {(['user_email', 'amount', 'status', 'created_at'] as const).map((col) => (
                    <th
                      key={col}
                      className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-indigo-700 transition"
                      onClick={() => handleSort(col)}
                    >
                      <div className="flex items-center gap-2">
                        {col === 'user_email' ? 'User' :
                         col === 'created_at' ? 'Requested On' :
                         col.charAt(0).toUpperCase() + col.slice(1)}
                        {sortColumn === col && (sortAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFiltered.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-6 py-4 font-medium text-gray-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.user_email || "—") }} />
                    <td className="px-6 py-4 font-mono text-indigo-700 font-semibold">${c.amount.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium ${
                        c.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        c.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(c.created_at).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </td>
                    <td className="px-6 py-4 space-x-3">
                      {c.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleAction(c.id, 'approve')}
                            className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition shadow-sm font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleAction(c.id, 'reject')}
                            className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition shadow-sm font-medium"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* MODAL */}
      <ConfirmModal
        isOpen={isOpen}
        message={message}
        onConfirm={onConfirm ?? (() => {})}
        onCancel={closeModal}
        headingLevel="h2"
      />
    </>
  );
}
